import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockResetIcon from "@mui/icons-material/LockReset";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import dayjs from "dayjs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageLayout from "../../components/PageLayout";
import { usePermissions } from "../../hooks/usePermissions";
import {
  PERMISSION_LABELS,
  PERMISSIONS,
  ROLES,
} from "../../utils/roleAccess";
import {
  createAdminAccount,
  deleteAdminAccount,
  listAdminAccounts,
  resetAdminAccountPassword,
  updateAdminAccount,
} from "../../http/adminAccounts";
import AdminAccountFormModal from "./AdminAccountFormModal";
import TempPasswordDialog from "./TempPasswordDialog";

const headCellSx = {
  fontWeight: 600,
  color: "#000",
  backgroundColor: "#e0e0e0",
  position: "sticky",
  top: -1,
  zIndex: 1,
  whiteSpace: "nowrap",
};

const PermissionChips = ({ account }) => {
  if (account.userType === ROLES.ADMIN) {
    return <Chip size="small" color="primary" label="Full access" />;
  }
  const pages = account.permissions.filter((k) => k !== PERMISSIONS.VIEW_ACTIVITY);
  const canViewActivity = account.permissions.includes(PERMISSIONS.VIEW_ACTIVITY);
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 420 }}>
      {pages.length === 0 && (
        <Chip size="small" variant="outlined" label="No pages" color="warning" />
      )}
      {pages.map((key) => (
        <Chip key={key} size="small" variant="outlined" label={PERMISSION_LABELS[key] || key} />
      ))}
      {canViewActivity && (
        <Tooltip title="Can see submission / post counts">
          <Chip size="small" color="info" label="Activity" />
        </Tooltip>
      )}
      {!account.hasExplicitPermissions && (
        <Tooltip title="Created before per-account permissions existed; still has the old intern page set. Edit to pin an explicit list.">
          <Chip size="small" variant="outlined" label="Legacy" />
        </Tooltip>
      )}
    </Box>
  );
};

const ManageAdminAccounts = () => {
  const { user: me } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tempPassword, setTempPassword] = useState(null); // { mode, account, temporaryPassword }
  const [confirm, setConfirm] = useState(null); // { type: 'reset'|'delete'|'toggle', account }
  const [busy, setBusy] = useState(false);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await listAdminAccounts();
      setAccounts(response.data?.data?.accounts || []);
    } catch (error) {
      console.error("Error fetching admin accounts:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleCreate = async (payload) => {
    const response = await createAdminAccount(payload);
    const data = response.data?.data || {};
    toast.success(`Account @${data.account?.username} created`);
    setTempPassword({ mode: "create", ...data });
    fetchAccounts();
  };

  const handleEdit = async (payload) => {
    const { password, ...rest } = payload;
    await updateAdminAccount(editing._id, rest);
    toast.success("Account updated");
    fetchAccounts();
  };

  const runConfirm = async () => {
    if (!confirm) return;
    const { type, account } = confirm;
    setBusy(true);
    try {
      if (type === "reset") {
        const response = await resetAdminAccountPassword(account._id);
        const data = response.data?.data || {};
        setTempPassword({ mode: "reset", ...data });
        toast.success(`Password reset for @${account.username}`);
      } else if (type === "delete") {
        await deleteAdminAccount(account._id);
        toast.success(`Account @${account.username} deleted`);
      } else if (type === "toggle") {
        await updateAdminAccount(account._id, { isDisabled: !account.isDisabled });
        toast.success(
          account.isDisabled
            ? `@${account.username} re-enabled`
            : `@${account.username} disabled and signed out`
        );
      }
      setConfirm(null);
      fetchAccounts();
    } catch (error) {
      console.error("Admin account action failed:", error);
    } finally {
      setBusy(false);
    }
  };

  const confirmCopy = {
    reset: {
      title: "Reset password",
      body: "A new temporary password will be generated and every active session of this account will be signed out. The account must set a new password on next sign-in.",
      action: "Reset password",
      color: "primary",
    },
    delete: {
      title: "Delete account",
      body: "This account will no longer be able to sign in to the admin panel. Content it created stays as is.",
      action: "Delete",
      color: "error",
    },
    toggle: {
      title: confirm?.account?.isDisabled ? "Re-enable account" : "Disable account",
      body: confirm?.account?.isDisabled
        ? "The account will be able to sign in again with its current password."
        : "The account is signed out immediately and cannot sign in until re-enabled. Its permissions are kept.",
      action: confirm?.account?.isDisabled ? "Re-enable" : "Disable",
      color: confirm?.account?.isDisabled ? "primary" : "warning",
    },
  };

  return (
    <PageLayout
      title="Admin Accounts"
      subtitle="Create restricted admin-panel accounts and choose exactly which pages each one can use"
      loading={loading}
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          sx={{ background: "#142C5C", "&:hover": { background: "#142C5C" } }}
        >
          New Account
        </Button>
      }
    >
      <Box
        sx={{
          padding: "15px 15px 0 15px",
          borderRadius: "6px",
          boxShadow: "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
        }}
      >
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: "calc(100vh - 250px)",
            boxShadow: "none",
            border: "1px solid rgba(224, 224, 224, 1)",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Username</TableCell>
                <TableCell sx={headCellSx}>Name</TableCell>
                <TableCell sx={headCellSx}>Role</TableCell>
                <TableCell sx={headCellSx}>Access</TableCell>
                <TableCell sx={headCellSx}>Status</TableCell>
                <TableCell sx={headCellSx}>Last active</TableCell>
                <TableCell sx={headCellSx} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ color: "#666", py: 5 }}>
                    {loading ? "Loading…" : "No accounts yet. Use “New Account” to create one."}
                  </TableCell>
                </TableRow>
              ) : (
                accounts.map((account) => {
                  const isFullAdmin = account.userType === ROLES.ADMIN;
                  const isMe = me?._id && String(me._id) === String(account._id);
                  return (
                    <TableRow key={account._id} hover sx={{ opacity: account.isDisabled ? 0.6 : 1 }}>
                      <TableCell sx={{ color: "#142C5C", fontWeight: 600 }}>
                        @{account.username}
                        {isMe && (
                          <Chip size="small" label="you" sx={{ ml: 1 }} variant="outlined" />
                        )}
                        {account.email && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {account.email}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: "#222" }}>
                        {[account.firstName, account.lastName].filter(Boolean).join(" ") || "—"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={isFullAdmin ? "Admin" : "Restricted"}
                          color={isFullAdmin ? "primary" : "default"}
                          variant={isFullAdmin ? "filled" : "outlined"}
                        />
                      </TableCell>
                      <TableCell>
                        <PermissionChips account={account} />
                      </TableCell>
                      <TableCell>
                        {account.isDisabled ? (
                          <Chip size="small" color="warning" label="Disabled" />
                        ) : account.mustChangePassword ? (
                          <Tooltip title="Has a temporary password; must change it on next sign-in">
                            <Chip size="small" color="info" variant="outlined" label="Temp password" />
                          </Tooltip>
                        ) : (
                          <Chip size="small" color="success" variant="outlined" label="Active" />
                        )}
                      </TableCell>
                      <TableCell sx={{ color: "#444", whiteSpace: "nowrap" }}>
                        {account.lastActiveAt
                          ? dayjs(account.lastActiveAt).format("MMM D, YYYY h:mm A")
                          : "Never"}
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        {isFullAdmin ? (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        ) : (
                          <>
                            <Tooltip title="Edit permissions">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditing(account);
                                  setFormOpen(true);
                                }}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reset password">
                              <IconButton
                                size="small"
                                onClick={() => setConfirm({ type: "reset", account })}
                              >
                                <LockResetIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={account.isDisabled ? "Re-enable" : "Disable"}>
                              <IconButton
                                size="small"
                                color={account.isDisabled ? "success" : "warning"}
                                onClick={() => setConfirm({ type: "toggle", account })}
                              >
                                {account.isDisabled ? (
                                  <CheckCircleOutlineIcon fontSize="small" />
                                ) : (
                                  <BlockIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setConfirm({ type: "delete", account })}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ py: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Restricted accounts sign in with their username. Pages they were not granted are
            hidden and the API rejects them. Submission and activity counts are hidden unless
            the account has the activity permission.
          </Typography>
        </Box>
      </Box>

      <AdminAccountFormModal
        open={formOpen}
        account={editing}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSubmit={editing ? handleEdit : handleCreate}
      />

      <TempPasswordDialog
        open={Boolean(tempPassword)}
        data={tempPassword}
        onClose={() => setTempPassword(null)}
      />

      <Dialog
        open={Boolean(confirm)}
        onClose={busy ? undefined : () => setConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: "#142C5C", fontWeight: 700 }}>
          {confirm ? confirmCopy[confirm.type].title : ""}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#444" }}>{confirm ? confirmCopy[confirm.type].body : ""}</Typography>
          {confirm && (
            <Typography sx={{ mt: 1.5, fontWeight: 600, color: "#142C5C" }}>
              @{confirm.account.username}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={runConfirm}
            variant="contained"
            color={confirm ? confirmCopy[confirm.type].color : "primary"}
            disabled={busy}
          >
            {busy ? "Working…" : confirm ? confirmCopy[confirm.type].action : ""}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer position="top-right" autoClose={3000} />
    </PageLayout>
  );
};

export default ManageAdminAccounts;
